import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Upload, Loader2, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { uploadDocumentToStorage } from "./upload-helpers";
import { dashboardKeys, type ExtractGenericResponse } from "@/lib/dashboard-api";

/**
 * Generic upload-and-extract modal used by Vendor Quote, AP Invoice, and
 * Goods Receipt power-pills. The full side-by-side Review modal is reserved
 * for Customer PO in this iteration; the three other kinds drop the user
 * onto the entity's existing detail page after a successful extract.
 */
interface ThinUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  entityType: 'vendor_quote' | 'ap_invoice' | 'goods_receipt';
  /** worker endpoint without leading /api */
  extractEndpoint: '/vendor-quotes/extract' | '/ap-invoices/extract' | '/goods-receipts/extract';
  /** Route to navigate to after a successful extraction; entityId is appended when known */
  detailRoutePrefix?: string;
}

export function ThinUploadModal({
  open, onOpenChange, title, entityType, extractEndpoint, detailRoutePrefix,
}: ThinUploadModalProps) {
  const { sessionId } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [extractedAccount, setExtractedAccount] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async (file: File) => {
      const slot = await uploadDocumentToStorage(file, entityType, sessionId);
      const res = await fetch(`/api${extractEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { Authorization: `Bearer ${sessionId}` } : {}),
        },
        body: JSON.stringify({ storageKey: slot.storageKey, originalFilename: slot.filename }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Extract failed');
      return json.data as ExtractGenericResponse;
    },
    onSuccess: (data) => {
      setExtractedAccount(data.accountMatch?.accountName ?? null);
      qc.invalidateQueries({ queryKey: dashboardKeys.automation() });
      toast({
        title: `${title} uploaded`,
        description: data.accountMatch
          ? `Matched ${data.accountMatch.accountName}. Opening for review…`
          : `Saved. Review and assign a vendor in the detail page.`,
      });
      // navigate after a beat so the toast is visible
      setTimeout(() => {
        onOpenChange(false);
        const id = (data as any).vendorQuoteId || (data as any).apInvoiceId || (data as any).goodsReceiptId;
        if (id && detailRoutePrefix) setLocation(`${detailRoutePrefix}/${id}`);
      }, 600);
    },
    onError: (e: Error) => toast({ title: `${title} failed`, description: e.message, variant: "destructive" }),
  });

  function handleFile(f: File) {
    setFileName(f.name);
    run.mutate(f);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload {title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {run.isPending ? (
            <div className="py-8 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-7 w-7 animate-spin text-purple-500" />
              <p className="text-sm font-medium">Reading {fileName}…</p>
              <p className="text-xs text-muted-foreground text-center max-w-xs">
                Power AI is extracting the document. Stay on this page.
              </p>
            </div>
          ) : run.isSuccess ? (
            <div className="py-8 flex flex-col items-center justify-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check className="h-5 w-5 text-emerald-500" />
              </div>
              <p className="text-sm font-medium">{title} uploaded</p>
              {extractedAccount && (
                <p className="text-xs text-muted-foreground">
                  Matched: <span className="font-medium">{extractedAccount}</span>
                </p>
              )}
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) handleFile(f);
              }}
              className="border-2 border-dashed rounded-lg py-10 text-center cursor-pointer hover:bg-muted/30 transition-colors"
            >
              <Upload className="h-7 w-7 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-medium">Drop a {title} here, or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, PNG, or JPG · up to 90 MB</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,image/*"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
